import { Button } from '@/components/ui/button';
import { Heart, Vote, TrendingUp, Users, Sparkles, ArrowRight, Calendar, MapPin, Mountain, Layers, Tag } from 'lucide-react';
import { CommunityVotingModal } from './CommunityVotingModal';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useCommunityVotingData } from '@/hooks/useCommunityVotingData';
import mensRetreatImage from '@/assets/mens-retreat-campfire.jpg';
import mistyForestBg from '@/assets/misty-forest.jpg';
import tropicalLeavesBg from '@/assets/tropical-leaves.jpg';
export const UpcomingExperiencesSection = () => {
  const navigate = useNavigate();
  const { data: votingData } = useCommunityVotingData();
  const [communityMembers, setCommunityMembers] = useState(0);
  const [totalVotes, setTotalVotes] = useState(0);
  
  useEffect(() => {
    if (votingData) {
      // Count unique session_ids for community members
      const uniqueSessions = new Set(votingData.map(record => record.session_id));
      setCommunityMembers(uniqueSessions.size);

      // Count total votes (sum of all category selections)
      let voteCount = 0;
      votingData.forEach(record => {
        voteCount += record.experience_categories?.length || 0;
      });
      setTotalVotes(voteCount);
    }
  }, [votingData]);
  const handleMensRetreatClick = () => {
    navigate('/experiences/mens-retreat');
  };
  const handleWomensRetreatClick = () => {
    navigate('/experiences/womens-retreat');
  };
  return <section id="experiences" className="py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-serif font-light mb-4 text-foreground">
            Upcoming Experiences
          </h2>
          <div className="w-24 h-px bg-accent/40 mx-auto"></div>
        </div>

        {/* Retreats Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-24">
          {/* Men's Retreat */}
          <div 
            onClick={handleMensRetreatClick} 
            className="group relative overflow-hidden rounded-lg cursor-pointer transition-all duration-500 hover:-translate-y-1"
            role="article"
            aria-label="Men's Retreat: Into the Wild - Weekend of brotherhood and nature immersion"
          >
            <div className="absolute inset-0 bg-cover bg-center opacity-30 brightness-75 group-hover:scale-110 transition-transform duration-700" style={{
            backgroundImage: `url(${mistyForestBg})`
          }}></div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
            
            <div className="relative p-12 flex flex-col min-h-[480px]">
              <div className="flex-grow space-y-4">
                <h3 className="text-4xl font-serif font-light text-white/95 leading-tight">
                  🏕️ Into the Wild
                </h3>
                
                <p className="text-base text-white/70 font-sans tracking-wide">
                  Men's Retreat
                </p>
                
                <p className="text-white/80 leading-relaxed max-w-md text-sm font-light">
                  A weekend of brotherhood, challenge, and reconnection.
                  Step away from routine and into the raw stillness of the mountains — where strength meets vulnerability, and wild nature mirrors your own.
                  Unplug, move, breathe, and rediscover what it means to belong — to yourself, to others, and to the land beneath your feet.
                </p>
              </div>

              <div className="space-y-6 pt-6">
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1.5 text-xs text-white/90 border border-white/30 rounded-full backdrop-blur-sm">
                    Brotherhood
                  </span>
                  <span className="px-3 py-1.5 text-xs text-white/90 border border-white/30 rounded-full backdrop-blur-sm">
                    Nature Immersion
                  </span>
                  <span className="px-3 py-1.5 text-xs text-white/90 border border-white/30 rounded-full backdrop-blur-sm">
                    Offline
                  </span>
                </div>

                <div className="flex items-center text-white/90 group-hover:text-white transition-colors">
                  <span className="text-sm font-light tracking-wide">Explore Retreat</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </div>

          {/* Women's Retreat */}
          <div 
            onClick={handleWomensRetreatClick} 
            className="group relative overflow-hidden rounded-lg cursor-pointer transition-all duration-500 hover:-translate-y-1"
            role="article"
            aria-label="Women's Retreat: Circle in the Mountains - Weekend of sisterhood and renewal"
          >
            <div className="absolute inset-0 bg-cover bg-center opacity-30 brightness-75 group-hover:scale-110 transition-transform duration-700" style={{
            backgroundImage: `url(${tropicalLeavesBg})`
          }}></div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
            
            <div className="relative p-12 flex flex-col min-h-[480px]">
              <div className="flex-grow space-y-4">
                <h3 className="text-4xl font-serif font-light text-white/95 leading-tight">
                  🌙 Circle in the Mountains
                </h3>
                
                <p className="text-base text-white/70 font-sans tracking-wide">
                  Women's Retreat
                </p>
                
                <p className="text-white/80 leading-relaxed max-w-md text-sm font-light">
                  A weekend of sisterhood, presence, and renewal.
                  In the calm of the mountains, you'll move between grounding and flow — reconnecting with your intuition, body, and natural rhythm.
                  Unplug, share, breathe, and rediscover what it means to return — to yourself, to others, and to the world that holds you.
                </p>
              </div>

              <div className="space-y-6 pt-6">
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1.5 text-xs text-white/90 border border-white/30 rounded-full backdrop-blur-sm">
                    Sisterhood
                  </span>
                  <span className="px-3 py-1.5 text-xs text-white/90 border border-white/30 rounded-full backdrop-blur-sm">
                    Inner Flow
                  </span>
                  <span className="px-3 py-1.5 text-xs text-white/90 border border-white/30 rounded-full backdrop-blur-sm">
                    Offline
                  </span>
                </div>

                <div className="flex items-center text-white/90 group-hover:text-white transition-colors">
                  <span className="text-sm font-light tracking-wide">Explore Retreat</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Co-Create With Us */}
        <div className="relative py-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card/60 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-border/50 shadow-lg">
              <div className="text-center mb-12">
                <h3 className="text-4xl md:text-5xl font-serif font-light mb-6 text-foreground">
                  Co-Create With Us
                </h3>
                <p className="text-lg text-muted-foreground max-w-xl mx-auto font-light leading-relaxed">
                  Your voice shapes the future of our gatherings
                </p>
              </div>

              {/* Stats Bar */}
              <div className="flex items-center justify-center gap-16 mb-12 flex-wrap">
                <div className="text-center">
                  <p className="text-4xl font-light mb-2 text-foreground">{communityMembers}</p>
                  <p className="text-sm text-muted-foreground font-light tracking-wide">Community Members</p>
                </div>
                <div className="w-px h-12 bg-border/40 hidden md:block"></div>
                <div className="text-center">
                  <p className="text-4xl font-light mb-2 text-foreground">{totalVotes}</p>
                  <p className="text-sm text-muted-foreground font-light tracking-wide">Votes Cast</p>
                </div>
                <div className="w-px h-12 bg-border/40 hidden md:block"></div>
                <div className="text-center">
                  <p className="text-4xl font-light mb-2 text-foreground">6</p>
                  <p className="text-sm text-muted-foreground font-light tracking-wide">Experience Types</p>
                </div>
              </div>
                
              <div className="text-center">
                <CommunityVotingModal trigger={<Button size="lg" className="px-12 py-4 text-lg font-medium hover-scale bg-accent hover:bg-accent/90 text-accent-foreground">
                      Share Your Vision
                    </Button>} />
              </div>
            </div>
          </div>
        </div>

        {/* Coming Soon Section */}
        <div className="relative mt-16">
          <div className="max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-accent/20 via-accent/10 to-background border border-accent/30 p-10 md:p-16">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(120,119,198,0.15),rgba(255,255,255,0))]"></div>
              
              <div className="relative text-center space-y-6">
                <h3 className="text-3xl md:text-4xl font-serif font-light text-foreground">The Forest is Expanding</h3>
                
                <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-light leading-relaxed">New <strong className="font-semibold">offline retreats</strong> and <strong className="font-semibold">city-based workshops</strong> are on their way. Stay connected to see them first.</p>
                
                <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mountain className="w-4 h-4 text-accent" />
                    <span>Retreats in Nature</span>
                  </div>
                  <div className="w-1 h-1 rounded-full bg-accent/40"></div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4 text-accent" />
                    <span>City-based Workshops</span>
                  </div>
                  <div className="w-1 h-1 rounded-full bg-accent/40"></div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4 text-accent" />
                    <span>Seasonal Events</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>;
};